import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { extractFrame } from '../../../../../../lib/utils/utils';
import { VideoContext } from '../../../../../../Context/VideoContext';

function TrimSlider({ min, max, step, onStartChange, onEndChange, start, end }) {
    const [startValue, setStartValue] = useState(min);
    const [endValue, setEndValue] = useState(end);

    const sliderRef = useRef(null);
    const handleStartRef = useRef(null);
    const handleEndRef = useRef(null);

    const video = useSelector((state) => state.lens.media.video);

    const { videoRef } = useContext(VideoContext);

    const [backgroundImage, setBackgroundImage] = useState('');

    const getPercentage = (value) => ((value - min) / (max - min)) * 100;

    const getEndPercentage = (value) => {
        const handle = handleEndRef.current.getBoundingClientRect();
        const rect = sliderRef.current.getBoundingClientRect();
        const handlePercent = (handle.width/rect.width) * 100;
        const percent = ((value - min) / (max - min)) * 100;
        return (percent - handlePercent);
    }

    const getBlurPercentage = () => ((max - end) / (max - min)) * 100;

    const [barPosition, setBarPosition] = useState(0);

    const handleStartMouseMove = (e) => {
        if (sliderRef.current) {
            const rect = sliderRef.current.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const newValue = Math.min(
                max,
                Math.max(min, min + Math.round((offsetX / rect.width) * (max - min) / step) * step)
            );
            if(newValue < end - 5) {
                setStartValue(newValue);
                onStartChange(newValue);
            }
        }
    };

    const handleEndMouseMove = (e) => {
        if (sliderRef.current) {
            const rect = sliderRef.current.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const newValue = Math.min(
                max,
                Math.max(min, min + Math.round((offsetX / rect.width) * (max - min) / step) * step)
            );
            if(newValue > start + 5) {
                setEndValue(newValue);
                onEndChange(newValue);
            }
        }
    };

    const handleStartMouseUp = () => {
        document.removeEventListener('mousemove', handleStartMouseMove);
        document.removeEventListener('mouseup', handleStartMouseUp);
    };

    const handleStartMouseDown = (e) => {
        document.addEventListener('mousemove', handleStartMouseMove);
        document.addEventListener('mouseup', handleStartMouseUp);
        handleStartMouseMove(e);
    };

    const handleEndMouseUp = () => {
        document.removeEventListener('mousemove', handleEndMouseMove);
        document.removeEventListener('mouseup', handleEndMouseUp);
    };

    const handleEndMouseDown = (e) => {
        document.addEventListener('mousemove', handleEndMouseMove);
        document.addEventListener('mouseup', handleEndMouseUp);
        handleEndMouseMove(e);
    };

    useEffect(() => {
        handleStartRef.current.style.left = `${getPercentage(startValue)}%`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startValue]);

    useEffect(() => {
        handleEndRef.current.style.left = `${getEndPercentage(endValue)}%`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endValue]);

    useEffect(() => {
        if (video) {
            extractFrame(video.url, 1).then((frame) => {
                setBackgroundImage(frame);
            }).catch((err) => {
                console.error('Error extracting frame:', err);
            });
        }
    }, [video]);

    // Get Bar Position

    useEffect(() => {
        const videoElement = videoRef.current;
        const sliderElement = sliderRef.current;

        if (videoElement && sliderElement) {
            const rect = sliderElement.getBoundingClientRect();
            const pixelsPerSecond = (rect.width - 5.5) / videoElement.duration;

            const handleTimeUpdate = () => {
                const positionFromLeft = videoElement.currentTime * pixelsPerSecond;
                setBarPosition(positionFromLeft);
            };

            videoElement.addEventListener("timeupdate", handleTimeUpdate);

            return () => {
                videoElement.removeEventListener("timeupdate", handleTimeUpdate);
            };
        }
    }, [videoRef, sliderRef, barPosition]);

    return (
        <div className="w-full py-2">
            <div
                className="w-full relative h-[52px] rounded-[7px] border-2 border-[#808080]"
                ref={sliderRef} 
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'contain'
                }}
            >
                <div 
                    style={{
                        left: `${barPosition}px`
                    }}
                    className="absolute w-[3.5px] bg-[#808080] h-[62px] transition-all delay-0 ease-linear rounded top-[-5px] z-10"
                ></div>

                <div
                    className="absolute top-[-1px] h-[52px] bg-[#0000008F] rounded-tl-[7px] rounded-bl-[7px]"
                    style={{ width: `${getPercentage(startValue)}%` }}
                ></div>
                <button onMouseDown={handleStartMouseDown} ref={handleStartRef} className='absolute outline-none left-0 top-0 flex items-center justify-center w-4 h-full bg-[#808080] rounded-tl rounded-bl'>
                   <div className="rounded h-5 w-[2.32px] bg-[#202020]"></div>
                </button>

                <button onMouseDown={handleEndMouseDown} ref={handleEndRef} className='absolute outline-none right-0 top-0 flex items-center justify-center w-4 h-full bg-[#808080] rounded-tr rounded-br'>
                    <div className="rounded h-5 w-[2.32px] bg-[#202020]"></div>
                </button>
                <div
                    className="absolute right-0 top-[-1px] h-[52px] bg-[#0000008F] rounded-tr-[7px] rounded-br-[7px]"
                    style={{ width: `calc(${getBlurPercentage(endValue)}%)` }}
                ></div>
            </div>

            <div className='text-center mt-2 font-normal text-[14px] leading-[22px] text-[#808080]'>00:{start < 10 ? `0${start}` : start} - 00:{end < 10 ? `0${parseInt(end)}` : parseInt(end)}</div>
        </div>
    );
}

export default TrimSlider;
